import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import auth from "../../firebase.init";
import { Helmet } from "react-helmet";

const PackagePaymentPending = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const fetchOrders = async () => {
    if (user?.email) {
      try {
        const response = await fetch(
          `https://allinone-clone-7d5d795e98ce.herokuapp.com/orders?userEmail=${user.email}`
        );
        const info = await response.json();
        // Sort latest by createdAt first
        const sortedInfo = info.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        setOrders(sortedInfo);
      } catch (error) {
        console.error("Error fetching standard orders:", error);
        toast.error("Could not load orders. Please try again.");
      }
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (user?.email) {
        setLoading(true);
        await fetchOrders();
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [user]);

  // --- Filtering Pending Orders ---
  const pendingOrders = orders.filter(
    (order) =>
      order.paymentStatus && order.paymentStatus.toLowerCase() === "pending"
  );

  // --- Pagination Logic ---
  const getPaginatedOrders = (data, page) => {
    const indexOfLastItem = page * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return data.slice(indexOfFirstItem, indexOfLastItem);
  };

  const paginatedOrders = getPaginatedOrders(pendingOrders, currentPage);
  const totalPages = Math.ceil(pendingOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // --- Utility Functions for UI ---
  const getStatusBadgeClass = (status) => {
    if (!status) return "bg-info text-white";
    status = status.toLowerCase();
    if (status === "pending") return "bg-warning text-white";
    if (status === "completed") return "bg-success text-white";
    return "bg-info text-white";
  };

  // Simplified link function
  const getPayNowLink = (order) => {
    return `/pay-now/${order._id}`;
  };

  // --- Loading State ---
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden"></span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Pending Orders | Your Website</title>
        <meta
          name="description"
          content="View and manage your pending orders. Complete payments to finalize your purchases."
        />
      </Helmet>

      <main className="premium-app-container py-5">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="glass-card border-0">
              <div className="card-body p-4">
                <h2 className="card-title fs-3 fw-bold mb-1">
                  My Pending Orders
                </h2>
                <p className="text-white-50 mb-0">
                  Manage all your pending payments for your purchases.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* --- Standard Orders Section --- */}
        <div className="glass-card border-0 shadow-sm">
          <div className="card-header bg-transparent py-3 border-bottom border-secondary">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="fs-4 fw-bold mb-0 text-white">
                Pending Orders ({pendingOrders.length})
              </h3>
            </div>
          </div>
          <div className="card-body p-0">
            {/* Desktop Table View */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-dark table-hover align-middle mb-0">
                <thead className="text-white border-bottom border-secondary">
                  <tr>
                    <th className="py-3 ps-4 text-white text-uppercase small">Order Info</th>
                    <th className="py-3 text-white text-uppercase small">Amount</th>
                    <th className="py-3 text-white text-uppercase small">Status</th>
                    <th className="py-3 text-end pe-4 text-white text-uppercase small">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.length > 0 ? (
                    paginatedOrders.map((order) => (
                      <tr key={order._id}>
                        <td className="py-3 ps-4">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-3 bg-dark border border-secondary d-flex align-items-center justify-content-center me-3"
                              style={{
                                width: "48px",
                                height: "48px",
                                overflow: "hidden",
                              }}
                            >
                              <img
                                src={
                                  order.featuredImage ||
                                  "/assets/img/dashboard/orders/o-1.png"
                                }
                                alt={order.packageName || "Order"}
                                className="img-fluid"
                                style={{
                                  objectFit: "cover",
                                  width: "100%",
                                  height: "100%",
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://placehold.co/48x48/e9ecef/6c757d?text=IMG";
                                }}
                              />
                            </div>
                            <div>
                              <h6 className="fw-bold mb-1 text-white">
                                {order.packageName || "Unnamed Package"}
                              </h6>
                              <div className="d-flex flex-column flex-md-row align-md-items-center">
                                <small className="text-white-50 me-md-3">
                                  ID: {order.orderId || "N/A"}
                                </small>
                                <small className="text-white-50 d-none d-md-block">
                                  <i className="bi bi-calendar-date me-1"></i>
                                  {order.orderDate || "N/A"}
                                </small>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className="fw-bold">
                            ${order.packagePrice || "0.00"}
                          </span>
                        </td>
                        <td className="py-3">
                          <span
                            className={`badge rounded-pill ${getStatusBadgeClass(
                              order.paymentStatus
                            )} px-3 py-2`}
                            style={{ fontSize: "0.85rem" }}
                          >
                            {order.paymentStatus
                              ? order.paymentStatus[0].toUpperCase() +
                                order.paymentStatus.slice(1)
                              : "Pending"}
                          </span>
                        </td>
                        <td className="py-3 text-end pe-4 d-none d-md-table-cell">
                          <Link
                            to={getPayNowLink(order)}
                            className="btn btn-primary btn-sm rounded-pill px-3 fw-medium"
                          >
                            <i className="bi bi-credit-card me-1"></i> Pay Now
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-5">
                        <div className="d-flex flex-column align-items-center">
                          <div className="mb-3">
                            <i className="bi bi-inbox fs-1 text-white-50 opacity-50"></i>
                          </div>
                          <h5 className="fw-semibold mb-2">
                            No Pending Orders
                          </h5>
                          <p className="text-white-50">
                            You're all caught up with your payments.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="d-block d-md-none">
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => (
                  <div key={order._id} className="glass-card mb-3 border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className="rounded-3 bg-dark border border-secondary d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "48px",
                            height: "48px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={
                              order.featuredImage ||
                              "/assets/img/dashboard/orders/o-1.png"
                            }
                            alt={order.packageName || "Order"}
                            className="img-fluid"
                            style={{
                              objectFit: "cover",
                              width: "100%",
                              height: "100%",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                "https://placehold.co/48x48/e9ecef/6c757d?text=IMG";
                            }}
                          />
                        </div>
                        <div>
                          <h6 className="fw-bold mb-1">
                            {order.packageName || "Unnamed Package"}
                          </h6>
                          <small className="text-white-50">
                            ID: {order.orderId}
                          </small>
                          <br />
                          <small className="text-white-50">
                            <i className="bi bi-calendar-date me-1"></i>{" "}
                            {order.orderDate || "N/A"}
                          </small>
                        </div>
                      </div>

                      <div className="mb-2">
                        <strong>Amount:</strong>{" "}
                        <span className="fw-bold">
                          ${order.packagePrice || "0.00"}
                        </span>
                      </div>

                      <div className="mb-2">
                        <strong>Status:</strong>{" "}
                        <span
                          className={`badge rounded-pill ${getStatusBadgeClass(
                            order.paymentStatus
                          )} px-3 py-2`}
                          style={{ fontSize: "0.85rem" }}
                        >
                          {order.paymentStatus
                            ? order.paymentStatus[0].toUpperCase() +
                              order.paymentStatus.slice(1)
                            : "Pending"}
                        </span>
                      </div>

                      <div className="text-center">
                        <Link
                          to={getPayNowLink(order)}
                          className="btn btn-primary rounded-pill px-4 py-2 fw-medium mt-2"
                        >
                          <i className="bi bi-credit-card me-1"></i> Pay Now
                        </Link>
                      </div>
                    </div>
                    <hr />
                  </div>
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-inbox fs-1 text-white-50 opacity-50 mb-3"></i>
                  <h5 className="fw-semibold">No Pending Orders</h5>
                  <p className="text-white-50">
                    You're all caught up with your payments.
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {pendingOrders.length > itemsPerPage && (
              <div className="d-flex justify-content-center py-3 border-top">
                <nav aria-label="Page navigation for orders">
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous"
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={`page-${i + 1}`}
                        className={`page-item ${
                          currentPage === i + 1 ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next"
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info Card */}
        <div className="glass-card border-0 shadow-sm mt-4">
          <div className="card-body p-4">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h4 className="fs-5 fw-bold mb-2">Complete Your Purchase</h4>
                <p className="mb-0 text-white-50">
                  Your orders are pending payment. Complete the payment process
                  to gain immediate access to your purchased items.
                </p>
              </div>
              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                {pendingOrders.length > 0 ? (
                  <Link
                    to={getPayNowLink(pendingOrders[0])}
                    className="btn btn-primary rounded-pill px-4 py-2 fw-medium"
                  >
                    <i className="bi bi-credit-card me-2"></i> Pay First Order
                  </Link>
                ) : (
                  <p className="text-white-50 mb-0 d-md-inline-block">
                    No payments due.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Add Bootstrap Icons CSS */}
      <Helmet>
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
        />
      </Helmet>
    </>
  );
};

export default PackagePaymentPending;
